import {
  APIComponentAttributes,
  APIComponentType,
  APIOrganizationUser,
  APIReferenceAttributes,
  APIReferenceType,
  ArdoqId,
  PROVENANCE_FIELD_KEYS,
  ReportColumn,
} from '@ardoq/api-types';
import { exhaustiveCheck, Maybe } from '@ardoq/common-helpers';
import { dateRangeOperations } from '@ardoq/date-range';
import { formatNumber } from '@ardoq/locale';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import {
  ComponentCellRendererProps,
  ReferenceCellRendererProps,
  SharedCellRendererProps,
  SharedCellRendererRow,
  TooltipParams,
} from './types';
import {
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
} from 'ag-grid-enterprise';
import {
  ColumnDefinitionArdoqMetadata,
  InventoryColumnDef,
  InventoryCommands,
  InventoryContext,
  SharedInventoryContext,
} from '../types';
import { EnhancedScopeData } from '@ardoq/data-model';
import { fieldOperations } from '@ardoq/core';
import { isEmpty } from 'lodash';
import { createComponentsOperations } from '../createComponents/createComponentsOperations';

type ValueParams<T> = { value?: T | null };

const getValue = <T = unknown>({ value }: ValueParams<T>): Maybe<T> =>
  value ?? null;

const getUser = ({
  value,
  context: { users },
}: ValueParams<string> & {
  context: Pick<SharedInventoryContext, 'users'>;
}): Maybe<APIOrganizationUser> => {
  return users.find(user => user._id === value) ?? null;
};

const getNumber = (
  {
    value,
    context: {
      scopeData,
      config: { locale },
    },
  }: ValueParams<number> & {
    context: Pick<SharedInventoryContext, 'scopeData' | 'config'>;
  },
  column: ReportColumn
): Maybe<string> => {
  const field = enhancedScopeDataOperations.getFieldByName(
    scopeData,
    column.key
  );
  if (value === null || typeof value === 'undefined') return null;

  return formatNumber(value, field?.numberFormatOptions ?? undefined, locale);
};

const getId = ({
  data,
}: {
  data?: Pick<SharedCellRendererRow, '_id'>;
}): Maybe<ArdoqId> => data?._id ?? null;

const getScopeData = ({
  context: { scopeData },
}:
  | ComponentCellRendererProps<any>
  | EditableCallbackParams<APIComponentAttributes, any, InventoryContext>
  | TooltipParams
  | FieldEditabilityCheckParams): EnhancedScopeData => scopeData;

const getCommands = ({
  context: { commands },
}:
  | ComponentCellRendererProps<any>
  | EditableCallbackParams<
      APIComponentAttributes,
      any,
      InventoryContext
    >): InventoryCommands => commands;

const getTypeId = ({
  data,
  context: { scopeData },
}:
  | ComponentCellRendererProps<any>
  | EditableCallbackParams<APIComponentAttributes, any, InventoryContext>
  | TooltipParams): Maybe<ArdoqId> =>
  data?.typeId ?? scopeData.componentsById[data?._id ?? '']?.typeId ?? null; // if report does not include typeId, we get it from scopeData

const getModelId = ({
  data,
}:
  | ComponentCellRendererProps<ArdoqId>
  | EditableCallbackParams<APIComponentAttributes, any, InventoryContext>
  | TooltipParams): Maybe<ArdoqId> => data?.model ?? null;

const getComponentType = (
  props:
    | ComponentCellRendererProps<any>
    | EditableCallbackParams<APIComponentAttributes, any, InventoryContext>
    | TooltipParams
): Maybe<APIComponentType> => {
  const modelId = getModelId(props);
  const typeId = getTypeId(props);
  const scopeData = getScopeData(props);
  if (!(modelId && typeId && scopeData)) return null;

  return enhancedScopeDataOperations.getComponentTypeByModelIdAndTypeId(
    scopeData,
    modelId,
    typeId
  );
};

const getReferenceType = <T>({
  data,
  context: { scopeData },
}: ReferenceCellRendererProps<T>): Maybe<APIReferenceType> => {
  if (!data) return null;

  return enhancedScopeDataOperations.getReferenceTypeByModelIdAndTypeId(
    scopeData,
    data.model,
    data.type
  );
};

const isRigidModel = (
  props: EditableCallbackParams<APIComponentAttributes, any, InventoryContext>
) => {
  const modelId = getModelId(props);
  const scopeData = getScopeData(props);
  return (
    scopeData &&
    modelId &&
    enhancedScopeDataOperations.isRigidModel(scopeData, modelId)
  );
};

/**
 * What the column-level editability checks need. Column-menu callbacks get no
 * cell params, so they pass the column definition on its own.
 */
export type FieldEditabilityCheckParams = {
  colDef: ColDef<APIComponentAttributes>;
  context: InventoryContext;
};

const hasDefinedField = <
  T extends
    | ComponentCellRendererProps<APIComponentAttributes>
    | EditableCallbackParams<APIComponentAttributes>
    | TooltipParams
    | FieldEditabilityCheckParams,
>(
  params: T
): params is T & { colDef: T['colDef'] & { field: string } } =>
  Boolean(params.colDef && 'field' in params.colDef && params.colDef.field);

const isApplicableReferenceColumn = (
  props:
    | ComponentCellRendererProps<any>
    | EditableCallbackParams<APIComponentAttributes, any, InventoryContext>
    | TooltipParams
): boolean => {
  if (
    !props.colDef ||
    !('field' in props.colDef) ||
    !props.data ||
    !props.colDef.field
  ) {
    return false;
  }
  const { metaModel, scopeData } = props.context;
  const componentType = getComponentType(props);
  const referenceTypeName = props.colDef.context.referenceTypeName;
  const modelId = getModelId(props);
  const isOutgoingReference = props.colDef.context.direction === 'outgoing';
  if (!referenceTypeName || !componentType || !metaModel || !modelId) {
    return false;
  }

  const hasTripleDefined = isOutgoingReference
    ? metaModel.triples.some(
        triple =>
          triple.sourceComponentType === componentType?.name &&
          triple.referenceType === referenceTypeName &&
          enhancedScopeDataOperations.referenceTypeExistsInModel(
            scopeData,
            modelId,
            referenceTypeName
          )
      )
    : metaModel.triples.some(
        triple =>
          triple.targetComponentType === componentType?.name &&
          triple.referenceType === referenceTypeName
      );

  const referencedComponentIds: ArdoqId[] =
    (props.data.derivedData?.[props.colDef.field] as ArdoqId[]) ?? [];

  return hasTripleDefined || referencedComponentIds.length > 0; // display references if there are any present, even if the triple is not defined
};

const isFieldApplicableToReference = (
  scopeData: EnhancedScopeData,
  fieldName: string,
  { type, model }: APIReferenceAttributes
): boolean => {
  const fields = enhancedScopeDataOperations.getFieldsByName(
    scopeData,
    fieldName
  );
  if (isEmpty(fields)) return true;

  return fields.some(field =>
    fieldOperations.appliesToReferenceType(field, type, model)
  );
};

const isFieldApplicableToComponent = (
  scopeData: EnhancedScopeData,
  fieldName: string,
  typeId: ArdoqId
): boolean => {
  const fields = enhancedScopeDataOperations.getFieldsByName(
    scopeData,
    fieldName
  );
  if (isEmpty(fields)) return true; // if the field is not in the scope data, it must be a default field and so it's applicable for all component types

  return fields.some(field => field.componentType.includes(typeId));
};

const isComponentColumnApplicable = (
  props:
    | ComponentCellRendererProps<any>
    | EditableCallbackParams<APIComponentAttributes>
    | TooltipParams
) => {
  // columns are based on fields. This functions checks if the field is defined for the component type for the cell that we are trying to render/edit.
  if (
    props.colDef &&
    (props.colDef as InventoryColumnDef).context.inventoryColumnType ===
      'reference'
  ) {
    return isApplicableReferenceColumn(props);
  }

  const scopeData = getScopeData(props);
  if (!(hasDefinedField(props) && props.data)) return false; // this should always be defined

  const typedId = getTypeId(props);
  if (!typedId) return false;

  return isFieldApplicableToComponent(scopeData, props.colDef.field, typedId);
};

const isReferenceFieldColumnApplicable = <T>({
  data,
  colDef,
  context: { scopeData },
}: ReferenceCellRendererProps<T>): boolean => {
  const fieldName = colDef?.field;
  if (!(fieldName && data)) return false;

  const reference = scopeData.referencesById[data._id];
  if (!reference) return false;

  return isFieldApplicableToReference(scopeData, fieldName, reference);
};

const isSharedColumnApplicable = <T>(
  props: SharedCellRendererProps<T>
): boolean => {
  switch (props.rowKind) {
    case 'component':
      return isComponentColumnApplicable(props);
    case 'reference':
      return isReferenceFieldColumnApplicable(props);
    default:
      return exhaustiveCheck(props);
  }
};

const getWorkspaceName = ({
  value,
  context: { scopeData },
}: ValueParams<ArdoqId> & {
  context: Pick<SharedInventoryContext, 'scopeData'>;
}): string => {
  if (!value) {
    return '';
  }
  return scopeData.workspacesById[value]?.name ?? 'Unknown Workspace';
};

/**
 * This happens for components that are created in the inventory and not persisted to the backend yet.
 */
const isComponentNotInScopeData = (props: ComponentCellRendererProps<any>) => {
  const id = getId(props);
  const scopeData = getScopeData(props);
  return !id || !scopeData.componentsById[id];
};

const hasEditPermissionForComponent = (
  params: TooltipParams | EditableCallbackParams<APIComponentAttributes>
) =>
  params.data &&
  params.context.accessControl.hasEditPermissionForComponent(params.data);

const hasReadPermissionForComponent = (
  data: APIComponentAttributes | undefined,
  context: Pick<InventoryContext, 'accessControl'>
): boolean =>
  Boolean(data && context.accessControl.hasReadPermissionForComponent(data));

const hasReadPermissionForComponentId = (
  context: Pick<InventoryContext, 'scopeData' | 'accessControl'>,
  componentId: ArdoqId
): boolean => {
  const component = context.scopeData.componentsById[componentId];
  return Boolean(
    component && context.accessControl.hasReadPermissionForComponent(component)
  );
};

const canOpenComponentOverview = (
  context: Pick<InventoryContext, 'config' | 'scopeData' | 'accessControl'>,
  componentId: ArdoqId
): boolean =>
  context.config.mode !== 'filterData' &&
  hasReadPermissionForComponentId(context, componentId);

const getOpenComponentOverview =
  (context: InventoryContext) =>
  (componentId: ArdoqId): VoidFunction | undefined =>
    canOpenComponentOverview(context, componentId)
      ? () =>
          context.commands.showComponentOverviewDrawer(
            componentId,
            context.scopeData
          )
      : undefined;

const isReadOnlyField = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes>
    | FieldEditabilityCheckParams
) => hasDefinedField(params) && isDefaultPropertyReadOnly(params.colDef.field);

const columnNamesThatAreNotEditable = new Set([
  '_id',
  '_version',
  'model',
  'component-key',
  'created',
  'createdBy',
  'lastModifiedBy',
  'lastUpdated',
  ...PROVENANCE_FIELD_KEYS,
]);

const isDefaultPropertyReadOnly = (key: string): boolean => {
  return columnNamesThatAreNotEditable.has(key);
};

const getFieldFromParams = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes>
    | FieldEditabilityCheckParams
) => {
  const scopeData = cellRendererPropsOperations.getScopeData(params);
  return hasDefinedField(params)
    ? enhancedScopeDataOperations.getFieldByName(scopeData, params.colDef.field)
    : null;
};

const isDerivedField = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes>
    | FieldEditabilityCheckParams
) => {
  const field = getFieldFromParams(params);

  return Boolean(field && fieldOperations.isLocallyDerivedField(field));
};

const isCalculatedField = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes>
    | FieldEditabilityCheckParams
): boolean => isCalculatedColumn(params.colDef);

const isLockedComponent = (
  params: TooltipParams | EditableCallbackParams<APIComponentAttributes>
) => Boolean(params.context?.isLockingEnabled && params.data?.lock);

const isNonMatchingAncestor = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes>
    | ComponentCellRendererProps<any>
): boolean => params.data?._is_match === false;

const isEditingCell = ({
  api,
  column,
  node,
}: ComponentCellRendererProps<any>) =>
  api
    .getEditingCells()
    .some(
      cell =>
        cell.colId === column?.getColId() && cell.rowIndex === node.rowIndex
    );

const isExternallyManagedFieldOnComponent = (
  params: TooltipParams | EditableCallbackParams<APIComponentAttributes>
) => {
  const component = params.data;
  return (
    component &&
    hasDefinedField(params) &&
    params.context.accessControl.isExternallyManagedFieldOnComponent(
      component,
      params.colDef.field
    )
  );
};

const isUnpersistedComponent = (
  params: TooltipParams | EditableCallbackParams<APIComponentAttributes>
): boolean =>
  Boolean(params.data) &&
  createComponentsOperations.isComponentNotPersisted(params.data!._id);

const getColumnMetadata = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes>
    | FieldEditabilityCheckParams
): ColumnDefinitionArdoqMetadata | undefined =>
  getColumnDefMetadata(params.colDef);

const getColumnDefMetadata = (
  colDef: ColDef<APIComponentAttributes> | ColGroupDef | null | undefined
): ColumnDefinitionArdoqMetadata | undefined =>
  colDef?.context as ColumnDefinitionArdoqMetadata | undefined;

const isCalculatedColumn = (
  colDef: ColDef<APIComponentAttributes> | ColGroupDef | null | undefined
): boolean => {
  const columnMetadata = getColumnDefMetadata(colDef);

  return (
    columnMetadata?.inventoryColumnType === 'field' &&
    columnMetadata.isCalculatedField
  );
};

const isTagsColumn = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes>
    | FieldEditabilityCheckParams
): boolean => getColumnMetadata(params)?.inventoryColumnType === 'tag';

const isLucidchartColumn = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes>
    | FieldEditabilityCheckParams
): boolean => getColumnMetadata(params)?.inventoryColumnType === 'lucidchart';

const isParentColumn = (
  params: TooltipParams | EditableCallbackParams<APIComponentAttributes>
): boolean => hasDefinedField(params) && params.colDef.field === 'parent';

const isReferenceColumn = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes>
    | FieldEditabilityCheckParams
): boolean => getColumnMetadata(params)?.inventoryColumnType === 'reference';

const requiresAnotherEntityChangeForUnpersistedComponent = (
  params: TooltipParams | EditableCallbackParams<APIComponentAttributes>
): boolean =>
  isUnpersistedComponent(params) &&
  (isTagsColumn(params) || isParentColumn(params) || isReferenceColumn(params));

/**
 * Minimal shape needed to decide whether a value may leave a cell (render,
 * tooltip, clipboard). Both AG Grid cell-renderer params and value-formatter
 * params satisfy it, so the redaction rule lives in a single place.
 */
type RedactionCheckParams = {
  data?: Pick<SharedCellRendererRow, 'rootWorkspace'>;
  colDef?: {
    field?: string;
    context?: ColumnDefinitionArdoqMetadata;
  } | null;
  context: Pick<SharedInventoryContext, 'accessControl'>;
};

const isConfidentialFieldUserCannotSee = (
  params: RedactionCheckParams
): boolean => {
  const fieldName = params.colDef?.field || null;
  const workspaceId = params.data?.rootWorkspace;

  if (!fieldName || !workspaceId) return false;

  const columnContext = params.colDef?.context as
    | ColumnDefinitionArdoqMetadata
    | undefined;
  const isDateRangeColumn =
    columnContext?.inventoryColumnType === 'field' &&
    columnContext.isDateRangeColumn;

  if (isDateRangeColumn) {
    const startFieldName = dateRangeOperations.toStartDateName(fieldName);
    const endFieldName = dateRangeOperations.toEndDateName(fieldName);

    return (
      !params.context.accessControl.canSeeConfidentialField(
        startFieldName,
        workspaceId
      ) ||
      !params.context.accessControl.canSeeConfidentialField(
        endFieldName,
        workspaceId
      )
    );
  }

  return !params.context.accessControl.canSeeConfidentialField(
    fieldName,
    workspaceId
  );
};

export const cellRendererPropsOperations = {
  getValue,
  getUser,
  getNumber,
  getId,
  getTypeId,
  getScopeData,
  getComponentType,
  getModelId,
  getReferenceType,
  isComponentColumnApplicable,
  isSharedColumnApplicable,
  isRigidModel,
  getWorkspaceName,
  isComponentNotInScopeData,
  getCommands,
  hasEditPermissionForComponent,
  hasReadPermissionForComponent,
  canOpenComponentOverview,
  getOpenComponentOverview,
  isReadOnlyField,
  isDerivedField,
  isCalculatedField,
  isCalculatedColumn,
  isTagsColumn,
  isLucidchartColumn,
  isReferenceColumn,
  isLockedComponent,
  isNonMatchingAncestor,
  isDefaultPropertyReadOnly,
  isEditingCell,
  isExternallyManagedFieldOnComponent,
  isConfidentialFieldUserCannotSee,
  requiresAnotherEntityChangeForUnpersistedComponent,
};
